import {useState, useEffect} from "react";
import {useLocation} from "react-router-dom";
import {useMutation} from "@apollo/client";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    TextField
} from "@mui/material";

import {
    GET_ALL_CONTENT_DATA,
    CREATE_CONTENT_FOLDER,
    UPDATE_CONTENT_FOLDER,
    DELETE_CONTENT_FOLDER
} from "../../../queries/GraphQLQueries";
import CustomAlert from "../../CustomAlert";
import {NODE_LEVEL} from "../../../domain/HierarchyConstants";
import {CONTENT_ACTION_CREATE, CONTENT_ACTION_DELETE, CONTENT_ACTION_RENAME} from "../ContentHierarchyTree";
import {useDialogNavigation} from "../../../hooks/useDialogNavigation";

export default function UpdateContentHierarchyDialog() {

    const handleClose = useDialogNavigation();
    const {state} = useLocation();

    const [alertMessage, setAlertMessage] = useState(null);
    const {data, crudAction} = state || {};
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [folderName, setFolderName] = useState('');
    const [enableContentAction, setEnableContentAction] = useState(false);
    const [isDelete, setIsDelete] = useState(false);
    const [confirmationButtonTitle, setConfirmationButtonTitle] = useState('');

    const [createContentFolder] = useMutation(CREATE_CONTENT_FOLDER, {refetchQueries: [{query: GET_ALL_CONTENT_DATA}, 'GetAllContentData']});
    const [updateContentFolder] = useMutation(UPDATE_CONTENT_FOLDER, {refetchQueries: [{query: GET_ALL_CONTENT_DATA}, 'GetAllContentData']});
    const [deleteContentFolder] = useMutation(DELETE_CONTENT_FOLDER, {refetchQueries: [{query: GET_ALL_CONTENT_DATA}, 'GetAllContentData']});

    const handleCloseCustomAlert = () => setAlertMessage(null);

    useEffect(() => {

        let dialogConfirmationButtonText = 'Create';
        let message = '';

        if (crudAction === CONTENT_ACTION_DELETE) {
            message = `Deleting the folder '${data.text}' will result in the moving its contents to the parent folder.`;
            dialogConfirmationButtonText = 'Delete';
            setIsDelete(true);
            setEnableContentAction(true);
        } else if (crudAction === CONTENT_ACTION_RENAME) {
            dialogConfirmationButtonText = 'Rename';
        }

        setMessage(message);
        setTitle(`${crudAction} Folder`);
        setConfirmationButtonTitle(dialogConfirmationButtonText);
    }, [crudAction]);

    const handleCreateContentFolder = () => {
        setAlertMessage({children: 'Creating folder...', severity: 'success'});
        const parentContentFolderId = data.type === NODE_LEVEL.BRANCH ? data.originalId : null;
        createContentFolder({
            variables: {
                name: folderName,
                serviceTypeKeycode: data.serviceTypeKeyCode,
                parentContentFolderId
            }
        }).then(() => handleClose())
        .catch(error => {
            setAlertMessage({children: error?.message, severity: 'error'});
            console.error(error);
        });
    }

    const handleRenameContentFolder = () => {
        setAlertMessage({children: 'Renaming folder...', severity: 'success'});
        updateContentFolder({
            variables: {
                contentFolderId: data.originalId,
                name: folderName
            }
        }).then(() => handleClose())
        .catch(error => {
            setAlertMessage({children: error?.message, severity: 'error'});
            console.error(error);
        });
    }

    const handleDeleteContentFolder = () => {
        setAlertMessage({children: 'Deleting folder...', severity: 'success'});
        deleteContentFolder({
            variables: {
                contentFolderId: data.originalId
            }
        }).then(() => handleClose())
        .catch(error => {
            setAlertMessage({children: error?.message, severity: 'error'});
            console.error(error);
        });
    }

    const handleContentUpdate = () => {

        if (crudAction === CONTENT_ACTION_CREATE) {
            handleCreateContentFolder();
        } else if (crudAction === CONTENT_ACTION_RENAME) {
            handleRenameContentFolder();
        } else {
            handleDeleteContentFolder();
        }
    }

    const handleInput = (value) => {
        setFolderName(value);
        setEnableContentAction(value !== "");
    }

    return (
      <>
          <Dialog open={true} onClose={handleClose}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {isDelete && <Typography variant={"body"}>{message}</Typography>}
                {!isDelete
                  && <TextField id='content-name'
                        label={'Folder Name'}
                        variant='standard'
                        required
                        autoComplete='off'
                        value={folderName}
                        onChange={(e) => handleInput(e.target.value)} />}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="contained">Cancel</Button>
                <Button disabled={!enableContentAction} onClick={handleContentUpdate} variant="contained">{confirmationButtonTitle}</Button>
            </DialogActions>
          </Dialog>
          <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert}/>
      </>
    );
}
